<template>
    <div class="redesign-export-data">
        <redesign-base-modal
            :visible="isOpen"
            @close="closeModal"
        >
            <template #title>
                {{ i18n.t('general.exportData') }}
            </template>
            <template #default>
                <div>
                    <div class="mb-4">
                        {{ i18n.t('widget.export.prepareExport') + ' ' + widget.Title }}
                    </div>
                    <div class="mb-3">
                        {{ i18n.t('widget.export.fileName') }}
                    </div>
                    <div class="mb-4">
                        <redesign-base-input v-model="widgetTitle" />
                    </div>
                    <div class="mb-3">
                        {{ i18n.t('report.format') }}
                    </div>
                    <div>
                        <redesign-radio-group
                            v-model="selectedExportOption"
                            class="mb-4"
                        >
                            <redesign-radio-button
                                v-for="item in EXPORT_OPTIONS"
                                :key="item.label"
                                :label="item.label"
                            >
                                {{ item.visibleLabel }}
                            </redesign-radio-button>
                        </redesign-radio-group>
                    </div>
                    <redesign-export-data-manage
                        ref="exportDataManageRef"
                        v-model="columnsToExport"
                        :visible-columns="localVisibleColumnsComputed"
                        :hidden-columns="localHiddenColumnsComputed"
                        :columns-to-export="columnsToExport"
                        @update-export-columns="updateColumnsToExport"
                    />
                </div>
            </template>
            <template #footer>
                <div class="flex gap-2">
                    <redesign-base-button
                        type="outline"
                        color="secondary"
                        @click="closeModal"
                    >
                        {{ i18n.t('common.cancel') }}
                    </redesign-base-button>
                    <redesign-base-button @click="exportData">
                        {{ i18n.t('widget.export.export') }}
                    </redesign-base-button>
                </div>
            </template>
        </redesign-base-modal>
    </div>
</template>

<script lang="ts" setup>
import { getWidgetDateInterval } from '@/helpers/widgetUtils.js'
import { timeFormatter } from '@/helpers/timeFormatter.js'
import { getInitialTime } from '@/util/timeUtils.js'
import { computed, ref } from 'vue'
import RedesignRadioButton from '@/components/RedesignBaseComponents/Inputs/RedesignRadioButton.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignExportDataManage
    from '@/components/RedesignComponents/RedesignTableComponents/RedesignExportDataManage.vue'
import XLSX from 'xlsx'
import { Call } from '@/types/extension'
import { PropNames } from '@/enum/realtimeTableConfig'
import i18n from '@/i18n.js'
import { ColumnsToExport, WidgetExtended, WidgetTemplateColumnWithConfig } from '@/types/dashboard'

type Headers = Record<string, string>;

const EXPORT_OPTIONS = {
    xlsx: {
        label: '.xlsx',
        visibleLabel: 'Exel'
    },
    csv: {
        label: '.csv',
        visibleLabel: 'CSV'
    }
}

const props = withDefaults(
    defineProps<{
        widget: WidgetExtended
        tableData: Record<string, string | number>[]
    }>(),
    {
        tableData: () => []
    }
)
//Emits
const emit = defineEmits<{
    (e: 'on-close-export-modal'): void
}>()

//State
const { closeWithConfirm } = useConfirmBeforeClose()
const isOpen = ref(false)
const columnsToExport = ref<ColumnsToExport>({})
const localAllColumns = ref<WidgetTemplateColumnWithConfig[]>([])
const selectedExportOption = ref(EXPORT_OPTIONS.xlsx.label)
const widgetTitle = ref(props.widget.Title)
const exportDataManageRef = ref<typeof RedesignExportDataManage>()

//Computed
const localVisibleColumnsComputed = computed<WidgetTemplateColumnWithConfig[]>(() => {
    if (!localAllColumns.value.length) {
        return []
    }
    return localAllColumns.value
        .filter(column => columnsToExport.value[column.prop]?.addToExport)
        .sort((a, b) => {
            const orderA = columnsToExport.value[a.prop].sortOrder
            const orderB = columnsToExport.value[b.prop].sortOrder
            return orderA - orderB
        })
})

const localHiddenColumnsComputed = computed(() => {
    if (!localAllColumns.value.length) {
        return []
    }
    return localAllColumns.value.filter(column => !columnsToExport.value[column.prop]?.addToExport)
})

const updateColumnsToExport = (newVal: ColumnsToExport) => {
    columnsToExport.value = newVal
}

const getFileName = () => {
    const fileName = widgetTitle.value || i18n.t('widget.title')
    const timeRange = getWidgetDateInterval(props.widget, 'DD-MM-YYYY')

    return fileName + ' ' + timeRange + selectedExportOption.value
}

const exportData = () => {
    const fileName = getFileName()
    const headers = localVisibleColumnsComputed.value.reduce((acc: Headers, column: WidgetTemplateColumnWithConfig) => {
        acc[column.prop] = column.label.fullTextLabel
        return acc
    }, {})

    const mappedData = props.tableData.map(row => {

        const formattedRow: Record<string, string | number> = {}

        Object.keys(headers).forEach(key => {
            formattedRow[key] = row[key]
        })

        if (formattedRow[PropNames.STATUS_DURATION]) {
            formattedRow[PropNames.STATUS_DURATION] = timeFormatter(+formattedRow[PropNames.STATUS_DURATION])
        }
        if (formattedRow[PropNames.CALL_INFO]) {
            formattedRow[PropNames.CALL_INFO] = i18n.t('widget.table.noCallers') as string
        }
        if (formattedRow[PropNames.CALL_INFO] && row.extension.calls.length) {
            formattedRow[PropNames.CALL_INFO] = row.extension.calls.reduce((acc: string, call: Call) => {
                const initialTime = getInitialTime(call.callStarted_UTC_CLIENT)
                acc += timeFormatter(initialTime, false) + ' ' + call.callType
                return acc
            }, '')
        }

        return formattedRow
    })

    const dataWithHeaders = [ headers, ...mappedData ]
    const worksheet = XLSX.utils.json_to_sheet(dataWithHeaders, { skipHeader: true })
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet)
    XLSX.writeFile(workbook, fileName)
    forceCloseModal()
}

function openModal (visibleColumns: Array<WidgetTemplateColumnWithConfig>, hiddenColumns: Array<WidgetTemplateColumnWithConfig>) {
    localAllColumns.value = [ ...visibleColumns, ...hiddenColumns ]
    visibleColumns.forEach((column, i) => {
        columnsToExport.value[column.prop] = {
            prop: column.prop,
            addToExport: true,
            sortOrder: i
        }
    })
    hiddenColumns.forEach((column) => {
        columnsToExport.value[column.prop] = {
            prop: column.prop,
            addToExport: false,
            sortOrder: 0
        }
    })
    isOpen.value = true
}

function forceCloseModal () {
    isOpen.value = false
    localAllColumns.value = []
    columnsToExport.value = {}
    exportDataManageRef.value?.clearData()
    emit('on-close-export-modal')
}

function closeModal () {
    closeWithConfirm(forceCloseModal)
}

defineExpose({
    open: openModal,
    close: forceCloseModal
})
</script>

